import { supabase } from '../lib/supabase';

/**
 * Generate a unique session ID for tracking form abandonment (not stored in session storage)
 */
export const generateSessionId = () => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Analyze form data to identify filled and missing fields
 */
const analyzeFormData = (formData, acceptsReferralTerms) => {
  const requiredFields = {
    full_name: 'الاسم الثنائي',
    email: 'البريد الإلكتروني',
    nsave_id: 'ايدي nsave',
    age: 'العمر',
    skills: 'المهارات',
    field: 'المجال',
    gender: 'الجنس',
    accepts_referral_terms: 'موافقة على شروط الإحالة'
  };

  const filled = [];
  const missing = [];

  // Check each required field
  Object.entries(requiredFields).forEach(([key, label]) => {
    if (key === 'accepts_referral_terms') {
      if (acceptsReferralTerms) {
        filled.push(label);
      } else {
        missing.push(label);
      }
    } else {
      const value = formData[key];
      if (value && value.toString().trim() !== '') {
        filled.push(label);
      } else {
        missing.push(label);
      }
    }
  });

  return { filled, missing };
};

/**
 * Save discarded form data (only called when user abandons the form)
 */
export const saveDiscardedForm = async (formData, acceptsReferralTerms) => {
  try {
    const sessionId = generateSessionId();
    const { filled, missing } = analyzeFormData(formData, acceptsReferralTerms);

    // Don't save if form is completely empty
    if (filled.length === 0) {
      return { success: true, message: 'No data to save' };
    }

    const discardedFormData = {
      session_id: sessionId,
      full_name: formData.full_name || null,
      email: formData.email || null,
      nsave_id: formData.nsave_id || null,
      age: formData.age ? parseInt(formData.age) : null,
      skills: formData.skills || null,
      field: formData.field || null,
      gender: formData.gender || null,
      referral: formData.referral || null,
      accepts_referral_terms: acceptsReferralTerms,
      filled_fields: filled,
      missing_fields: missing,
      last_updated: new Date().toISOString(),
      user_agent: navigator.userAgent
    };

    // Insert new record (no need to check for existing since we're not tracking sessions anymore)
    const { error: insertError } = await supabase
      .from('discarded_forms')
      .insert([discardedFormData]);

    if (insertError) {
      console.error('Error inserting discarded form:', insertError);
      return { success: false, error: insertError };
    }

    return { success: true };
  } catch (error) {
    console.error('Error saving discarded form:', error);
    return { success: false, error };
  }
};

// Removed deleteDiscardedForm and getCurrentSessionForm - no longer needed without session storage

/**
 * Fetch all discarded forms (for admin view)
 */
export const getDiscardedForms = async () => {
  try {
    const { data, error } = await supabase
      .from('discarded_forms')
      .select('*')
      .order('last_updated', { ascending: false });

    if (error) {
      console.error('Error fetching discarded forms:', error);
      return { success: false, error };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error fetching discarded forms:', error);
    return { success: false, error };
  }
};

/**
 * Get statistics about discarded forms
 */
export const getDiscardedFormsStats = async () => {
  try {
    const { data, error } = await supabase
      .from('discarded_forms')
      .select('missing_fields, filled_fields');

    if (error) {
      console.error('Error fetching discarded forms stats:', error);
      return { success: false, error };
    }

    // Calculate most commonly missed fields
    const missedFieldsCount = {};
    const filledFieldsCount = {};
    
    data.forEach(form => {
      form.missing_fields?.forEach(field => {
        missedFieldsCount[field] = (missedFieldsCount[field] || 0) + 1;
      });
      form.filled_fields?.forEach(field => {
        filledFieldsCount[field] = (filledFieldsCount[field] || 0) + 1;
      });
    });

    return {
      success: true,
      stats: {
        total: data.length,
        missedFieldsCount,
        filledFieldsCount,
        averageFilled: data.length > 0 
          ? (data.reduce((sum, form) => sum + (form.filled_fields?.length || 0), 0) / data.length).toFixed(1)
          : 0,
        averageMissing: data.length > 0
          ? (data.reduce((sum, form) => sum + (form.missing_fields?.length || 0), 0) / data.length).toFixed(1)
          : 0
      }
    };
  } catch (error) {
    console.error('Error calculating discarded forms stats:', error);
    return { success: false, error };
  }
};

/**
 * Delete a specific discarded form (Admin only)
 */
export const deleteDiscardedFormById = async (formId) => {
  try {
    const { error } = await supabase
      .from('discarded_forms')
      .delete()
      .eq('id', formId);

    if (error) {
      console.error('Error deleting discarded form:', error);
      return { success: false, error };
    }

    return { success: true };
  } catch (error) {
    console.error('Error deleting discarded form:', error);
    return { success: false, error };
  }
};

